import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Trash2, Copy, Check, Eye, EyeOff, Lock, Globe, Building2, Clock, User, Folder, Key, RefreshCw, History, Share2, ExternalLink, AlertTriangle } from 'lucide-react';
import { vaultAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import RelatedItems from '../../components/documentation/RelatedItems';
import Attachments from '../../components/documentation/Attachments';
import { isInternalRole } from '../../utils/roles.js';

// Visibility badge
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal Only', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company Shared', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${className}`}>
      <Icon className="w-4 h-4" />
      {label}
    </span>
  );
}

// Reveal field component
function RevealField({ label, field, onReveal, isRevealed, value, canCopy = true }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
        <div className="flex items-center gap-2">
          {isRevealed && canCopy && (
            <button
              onClick={handleCopy}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => onReveal(field)}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
            title={isRevealed ? 'Hide' : 'Reveal'}
          >
            {isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="font-mono text-lg text-neutral-900 dark:text-white">
        {isRevealed ? value || '(empty)' : '••••••••••••'}
      </div>
    </div>
  );
}

// TOTP display component - Live code generation with countdown
function TOTPDisplay({ passwordId }) {
  const [code, setCode] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [copied, setCopied] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [error, setError] = useState(null);

  // Fetch TOTP code from backend
  const { mutate: fetchTotp, isPending } = useMutation({
    mutationFn: () => vaultAPI.getTOTP(passwordId),
    onSuccess: (response) => {
      setCode(response.data.code);
      setTimeRemaining(response.data.timeRemaining);
      setIsRevealed(true);
      setError(null);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to get TOTP code');
    },
  });

  // Countdown timer with auto-refresh
  useEffect(() => {
    if (!isRevealed) return;

    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = 30 - (now % 30);
      setTimeRemaining(remaining);

      // Auto-refresh when timer expires
      if (remaining === 30) {
        fetchTotp();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isRevealed, fetchTotp]);

  const handleCopy = async () => {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const progress = (timeRemaining / 30) * 100;

  // Format code with space in middle (123 456)
  const formattedCode = code ? `${code.slice(0, 3)} ${code.slice(3)}` : '';

  return (
    <div className="p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
          One-Time Password (TOTP)
        </span>
        {!isRevealed && (
          <button
            onClick={() => fetchTotp()}
            disabled={isPending}
            className="text-sm text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-50"
          >
            {isPending ? 'Loading...' : 'Show Code'}
          </button>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 mb-2">
          {error}
        </div>
      )}

      {isRevealed && code && (
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="font-mono text-4xl font-bold text-neutral-900 dark:text-white tracking-[0.3em]">
              {formattedCode}
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              Code refreshes automatically
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              title="Copy code"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setIsRevealed(false)}
              className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              title="Hide"
            >
              <EyeOff className="w-5 h-5" />
            </button>
          </div>
          {/* Circular countdown timer */}
          <div className="relative w-14 h-14">
            <svg className="w-14 h-14 -rotate-90">
              <circle
                cx="28"
                cy="28"
                r="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                className="text-neutral-200 dark:text-neutral-700"
              />
              <circle
                cx="28"
                cy="28"
                r="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeDasharray={`${progress * 1.5} 150.8`}
                strokeLinecap="round"
                className={timeRemaining <= 5 ? 'text-red-500' : 'text-brand-500'}
              />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${
              timeRemaining <= 5 ? 'text-red-500' : 'text-neutral-700 dark:text-neutral-300'
            }`}>
              {timeRemaining}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Rotation history modal
function RotationHistoryModal({ passwordId, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['vault', 'password', passwordId, 'history'],
    queryFn: () => vaultAPI.getPasswordHistory(passwordId),
  });

  const history = data?.data?.history || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Password Rotation History</h2>
        </div>
        <div className="overflow-y-auto max-h-[60vh] p-4">
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-neutral-100 dark:bg-neutral-700 rounded" />
              ))}
            </div>
          ) : history.length > 0 ? (
            <div className="space-y-3">
              {history.map((entry, index) => (
                <div key={entry.id} className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-900 dark:text-white">
                      Rotation #{history.length - index}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {new Date(entry.rotated_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                    By {entry.rotated_by_email}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-neutral-500 py-8">No rotation history</p>
          )}
        </div>
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Access management modal
function AccessModal({ passwordId, onClose }) {

  const { data, isLoading } = useQuery({
    queryKey: ['vault', 'password', passwordId, 'access'],
    queryFn: () => vaultAPI.listAccess(passwordId),
  });

  // Revoking used to throw ReferenceError here: no queryClient was ever created.
  const queryClient = useQueryClient();
  const revokeMutation = useMutation({
    mutationFn: (accessId) => vaultAPI.revokeAccess(passwordId, accessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault', 'password', passwordId, 'access'] });
    },
  });

  const access = data?.data?.access || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Access Management</h2>
        </div>
        <div className="overflow-y-auto max-h-[60vh] p-4">
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="h-16 bg-neutral-100 dark:bg-neutral-700 rounded" />
              ))}
            </div>
          ) : access.length > 0 ? (
            <div className="space-y-3">
              {access.map((grant) => (
                <div key={grant.id} className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-900 dark:text-white">
                      {grant.user_email || `Company ID: ${grant.company_id}`}
                    </p>
                    <p className="text-sm text-neutral-500">
                      {grant.access_level} access • Granted {new Date(grant.granted_at).toLocaleDateString()}
                      {grant.expires_at && ` • Expires ${new Date(grant.expires_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button
                    onClick={() => revokeMutation.mutate(grant.id)}
                    disabled={revokeMutation.isPending}
                    className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-neutral-500 py-8">No access grants</p>
          )}
        </div>
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PasswordDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [revealed, setRevealed] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isInternal = isInternalRole(user?.role);
  const isAdmin = user?.role === 'admin';

  // Fetch password
  const { data, isLoading, error } = useQuery({
    queryKey: ['vault', 'password', id],
    queryFn: () => vaultAPI.getPassword(id),
  });

  const password = data?.data?.password;

  // Reveal mutation
  const revealMutation = useMutation({
    mutationFn: ({ field }) => vaultAPI.revealField(id, field),
    onSuccess: (response, { field }) => {
      setRevealed(prev => ({ ...prev, [field]: response.data.value }));
      // Auto-hide after 30 seconds
      setTimeout(() => {
        setRevealed(prev => ({ ...prev, [field]: null }));
      }, 30000);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => vaultAPI.deletePassword(id),
    onSuccess: () => {
      navigate('/vault');
    },
  });

  const handleReveal = (field) => {
    if (revealed[field]) {
      setRevealed(prev => ({ ...prev, [field]: null }));
    } else {
      revealMutation.mutate({ field });
    }
  };

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="mb-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-64 animate-pulse" />
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2 mb-4 animate-pulse" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-neutral-100 dark:bg-neutral-900 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !password) {
    return (
      <div className="animate-fade-in">
        <div className="text-center py-12">
          <Key className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">Password Not Found</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The password you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link
            to="/vault"
            className="inline-flex items-center gap-2 text-brand-600 dark:text-brand-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Vault
          </Link>
        </div>
      </div>
    );
  }

  const isExpiring = password.expires_at && new Date(password.expires_at) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <div className="animate-fade-in">
      {/* Back link */}
      <Link
        to="/vault"
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Vault
      </Link>

      {/* Header */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 mb-6">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-3">
                {password.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                {isInternal && <VisibilityBadge visibility={password.visibility} />}
                {password.password_type && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
                    {password.password_type.replace('_', ' ')}
                  </span>
                )}
                {isExpiring && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                    <AlertTriangle className="w-4 h-4" />
                    Expiring {new Date(password.expires_at).toLocaleDateString()}
                  </span>
                )}
                {password.allow_customer_reveal && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    Customer Reveal Enabled
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            {isInternal && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowHistory(true)}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Rotation History"
                >
                  <History className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowAccess(true)}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Access Management"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                <Link
                  to={`/vault/${id}/edit`}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit className="w-5 h-5" />
                </Link>
                {isAdmin && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-neutral-500 dark:text-neutral-400">
            {password.folder_name && (
              <span className="flex items-center gap-1.5">
                <Folder className="w-4 h-4" />
                {password.folder_name}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              Updated {new Date(password.updated_at).toLocaleDateString()}
            </span>
            {password.updated_by_email && (
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {password.updated_by_email}
              </span>
            )}
            {password.last_rotated_at && (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="w-4 h-4" />
                Last rotated {new Date(password.last_rotated_at).toLocaleDateString()}
              </span>
            )}
          </div>

          {password.description && (
            <p className="mt-4 text-neutral-600 dark:text-neutral-400">
              {password.description}
            </p>
          )}
        </div>
      </div>

      {/* Credentials */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Credentials</h2>

        {password.username_hint && (
          <RevealField
            label="Username"
            field="username"
            passwordId={id}
            onReveal={handleReveal}
            isRevealed={!!revealed.username}
            value={revealed.username}
          />
        )}

        <RevealField
          label="Password"
          field="password"
          passwordId={id}
          onReveal={handleReveal}
          isRevealed={!!revealed.password}
          value={revealed.password}
        />

        {password.url_encrypted && (
          <div className="p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">URL</span>
              {revealed.url && (
                <a
                  href={revealed.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 dark:text-brand-400 hover:underline text-sm flex items-center gap-1"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {revealed.url ? (
              <div className="font-mono text-lg text-neutral-900 dark:text-white break-all">
                {revealed.url}
              </div>
            ) : (
              <button
                onClick={() => handleReveal('url')}
                className="text-brand-600 dark:text-brand-400 hover:underline"
              >
                Reveal URL
              </button>
            )}
          </div>
        )}

        {password.notes_encrypted && (
          <RevealField
            label="Notes"
            field="notes"
            passwordId={id}
            onReveal={handleReveal}
            isRevealed={!!revealed.notes}
            value={revealed.notes}
          />
        )}

        {password.otp_type && (
          <TOTPDisplay passwordId={id} />
        )}
      </div>

      {/* Related Items */}
      <div className="mt-6">
        <RelatedItems
          sourceType="password"
          sourceId={id}
          companyId={password.company_id}
        />
      </div>

      {/* Attachments */}
      <div className="mt-6">
        <Attachments
          assetType="password"
          assetId={id}
          companyId={password.company_id}
        />
      </div>

      {/* Modals */}
      {showHistory && (
        <RotationHistoryModal passwordId={id} onClose={() => setShowHistory(false)} />
      )}

      {showAccess && (
        <AccessModal passwordId={id} onClose={() => setShowAccess(false)} />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Delete Password?
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Are you sure you want to delete "{password.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
